import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { type Tier, getAllowedMarkerFormats, getDefaultMarkerFormat } from "shared/marker-modes";
import type { MarkerFormat, UserPublic } from "shared/schemas";
import { ME_QUERY_KEY, patchUserSettings } from "./use-auth.js";

const STORAGE_KEY = "anonymize.markerFormat";

// Phase 3.4 (2026-06-26): `redacted` retired from MarkerFormat. Stored values
// that still hold `"redacted"` (from old sessions before the migration runs,
// or from a stale browser tab) fall through readStored() as null and the hook
// falls back to getDefaultMarkerFormat(tier).
const ALL_VALUES = new Set<MarkerFormat>(["default", "custom", "xxxxx", "blackbar", "pseudonym"]);

function readStored(): MarkerFormat | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v && ALL_VALUES.has(v as MarkerFormat) ? (v as MarkerFormat) : null;
}

const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): MarkerFormat | null {
  return readStored();
}

function getServerSnapshot(): MarkerFormat | null {
  return null;
}

/**
 * Mirror without touching localStorage. See use-theme.ts:applyAndNotify for
 * the cross-tab-ping-pong rationale (regression test in
 * use-marker-format.test.tsx).
 */
function applyAndNotify(): void {
  for (const l of listeners) l();
}

export function writeMarkerFormatLocal(next: MarkerFormat): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, next);
  }
  applyAndNotify();
}

/**
 * Marker-format preference shared across the RedactForm widget and the
 * Account settings page. Mirrors useTheme: module-level store for cross-
 * instance consistency, PATCH to /users/me when a session exists.
 *
 * The returned `markerFormat` is always within `getAllowedMarkerFormats(tier)`.
 * If storage holds a disallowed value, the effective value falls back to
 * `getDefaultMarkerFormat(tier)` at read time — storage is NOT rewritten,
 * so a Pro upgrade restores the user's original preference.
 */
export function useMarkerFormat(tier: Tier) {
  const rawStored = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const qc = useQueryClient();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      applyAndNotify();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const allowed = getAllowedMarkerFormats(tier);
  const markerFormat: MarkerFormat =
    rawStored && allowed.includes(rawStored) ? rawStored : getDefaultMarkerFormat(tier);

  const setMarkerFormat = useCallback(
    (next: MarkerFormat) => {
      if (!allowed.includes(next)) return;
      writeMarkerFormatLocal(next);
      const user = qc.getQueryData<UserPublic | null>(ME_QUERY_KEY);
      if (user && user.markerFormat !== next) {
        void patchUserSettings(qc, { markerFormat: next });
      }
    },
    [qc, allowed],
  );

  return { markerFormat, setMarkerFormat };
}
